import mvc from './MVC.png'
import mvcPurposes from './MVC-purposes.png'
import whereToPutDomainLogic from './where-to-put-domain-logic.png'
import domain from './domain.png'
import repository from './repository.png'

const content = `<p>MVC stands for Model View Controller. It is a way to structure the code of an application.</p>

<p>The general idea of MVC is to split the code into 3 kinds of elements:</p>

<ul><li>the Model contains code that represents the data</li><li>the View contains code that describes what to display to the user</li><li>the Controller contains code that orchestrates the other elements to handle user actions</li></ul>

<p>MVC allows to achieve <a href="/high-cohesion">High cohesion</a>, and uses <a href="/pure-fabrication">Pure fabrication</a> with controllers. However the MVC pattern has its limits, and can reduce cohesion depending how we use it.</p>

<h2 id="The-MVC-pattern">The MVC pattern</h2>

<p>There are many variations of the MVC pattern, and they mainly differ on the interactions between models, views, and controllers.</p>

<p>To describe the MVC pattern, let’s examine one implementation of it: the MVC of Rails. We’ll see how it allows to achieve high cohesion, and its pitfalls that can become detrimental to cohesion.</p>

<p>In Rails MVC is used for the backend of websites. The View is not exactly what is displayed to the user, but rather what the server returns to the browser in response to an HTTP request.</p>

<p>The Controller is where the execution flow arrives when the HTTP request is received (more precisely, the HTTP request is examined by the web server, and depending on its url it gets routed to a method of a specific controller).</p>

<p>The purpose of the Controller is to handle the HTTP request and produce the data that the View will use to build the HTTP response.</p>

<p>To do this, the Controller reaches to the Model, which accesses the database to retrieve or update the data. There is one view for each request that the controller handles, but the controller can collect data from several models to serve one request:</p>

<img src=${mvc} alt="MVC" />

<h3 id="Simple-example">Simple example</h3>

<p>Let’s take a simple example to illustrate, with a blog. A blog has articles, and each article can have comments.</p>

<p>An example of HTTP request contains the url <em>https://www.myblog.com/articles/how-to-get-rich-quickly</em>, that reaches the following controller method:</p>

<pre><code class="language-ruby">class ArticlesController &lt; ApplicationController
  def show
    slug = params&#91;:id]
    @article = Article.find_by(slug: slug)
    @comments = Comment.where(article: article)
  end
end</code></pre>

<p>This controller does everything that contributes to handling the HTTP request:</p>

<ul><li>the <code>show</code> method is where a request path of the form <code>/ressources/id</code> generally gets routed to</li><li>the controller extracts the parameters from the HTTP requests contained in <code>params</code></li><li>the controller knows which data is needed for the HTTP response: the article and the comments</li><li>the controller orchestrates the rest of the code to put the data at the disposal of the view</li></ul>

<p>This was for the controller. The models, on their side, describe the relations in the data:</p>

<pre><code class="language-ruby">class Article &lt; ApplicationRecord
  has_many :comments
end

class Comment &lt; ApplicationRecord
  belongs_to :article
end</code></pre>

<p>There can be more to it in data representation in model, for example validation code that ensures the data respects some properties and throws an error otherwise.</p>

<p>Finally, the view uses the variables set by the controller to format the HTTP response into a JSON document, an HTML page, or another kind of format. Here is a Rails view generating a JSON:</p>

<pre><code class="language-ruby">json.title @article.title
json.body @article.body
json.comments do
  json.array! @comments do |comment|
    json.author comment.author
    json.content comment.content
    json.date comment.date
  end
end</code></pre>

<p>The generated JSON can look like this:</p>

<pre><code class="language-ruby">{
  "title": "How to Get Rich Quickly",
  "body": "Find 10 jobs that pay 100$ an hour. Multitask.",
  "comments": &#91;
    {
      "author": "RichLad97",
      "content": "Exactly what I did, it works!",
      "date": "25/10/2022"
    },
    {
      "author": "Bloke72",
      "content": "Inspiring, thanks for sharing.",
      "date": "26/10/2022"
    }
  ]
}</code></pre>

<p>All three of the model, view and controller are focused around their own goal. This give the code <a href="/high-cohesion">High cohesion</a>:</p>

<img src=${mvcPurposes} alt="MVC-purposes" />

<p>The controller is a typical example of a <a href="/pure-fabrication">Pure fabrication</a>, that doesn’t have an equivalent object in the domain of the application.</p>

<h2 id="The-limits-of-MVC">The limits of MVC</h2>

<p>MVC covers the design needs of an application that simply displays data, such as a blog. But when the application has domain logic, the MVC reaches its limits.</p>

<p>The domain logic is typically what your product teams describes as what the application should do. Typical examples are algorithms (what suggestions to present to the user, how to price a product, how to determine a driving route, etc.), rules (what actions are allowed in a given context, what happens when the user updates their address, etc.) and many other domain concerns.</p>

<p>Adding domain logic to models, views or controllers makes them lose their cohesion, which was one of their main assets. The MVC offers no place to put the domain logic:</p>

<img src=${whereToPutDomainLogic} alt="where-to-put-domain-logic" />

<p>Instead, an approach that maintains cohesion in the code is to have other classes, on top of MVC, that are dedicated to the domain logic.</p>

<p>Typically, controllers call those classes. This also has the advantage to make the domain logic independent and reusable across HTTP endpoints and other call sites such as batch scripts and tests.</p>

<img src=${domain} alt="domain" />

<p>In the above schema, the domain logic accesses data directly from models. Another option is that the controller passes the data to the domain logic.</p>

<p>If the controller doesn’t know in advance what data the domain logic will need or if the data representation is itself complex, the controller can pass a repository object, that is a class that presents an interface independent from the type of storage and provides the data to the domain logic when it needs it:</p>

<img src=${repository} alt="repository" />

<p>This is the structure of <a href="/hexagonal-architecture">Hexagonal architecture</a> (that has nothing to do with hexagons unfortunately) where we see more examples of repository objects.</p>

<p>Note that all these examples represented use cases to get data, but the structure is the same for requests that perform modifications of data.</p>

<p>In conclusion, MVC is a great pattern for simple websites to increase code cohesion, and works well as a complement of the domain logic for elaborate applications.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>MVC splits technical responsibilities into 3 classes with high cohesion</li><li>The Model represents the data</li><li>The Controller handles the user request</li><li>The View represents the response</li><li>Many variations of MVCs define different interactions between those 3 elements</li><li>MVC leaves no place to the domain code without losing cohesion</li><li>Create other classes to handle domain code, that are called by the controller</li></ul>
</div>`

export default content
